/**
 * Opportunity Response Processor
 * 
 * This module contains business logic for processing OOT (Opportunity) API responses.
 * It handles the complex state management and conditional logic that occurs after
 * fetching opportunity data from the backend.
 */

import _ from "lodash";
import { Dispatch } from "@reduxjs/toolkit";
import {
  handleSellToParty,
  setError,
  setNetworkIds,
  setOOTAuth,
  setOOTStatus,
} from "Store/auth";
import { setOBJ, setStatus } from "Store/contentState/caseDetailsSlice";
import { setLive } from "Store/theme";
import {
  STATUS,
  caseCategory,
  nonSCRMCaseTypes,
} from "dot/Components/Misc/CaseDetails_System";
import {
  handleAccountLogic,
  handleExceptionR6InternalLogic,
} from "dot/Components/Logics/systemLogics";
import { setFormLoaded } from "Store/contentState/findCaseSlice";

/**
 * Context for processing opportunity response
 * Contains the current state and flags needed for conditional logic
 */
export interface OpportunityProcessorContext {
  caseType: string;
  subType: string;
  scrmId: string;
  noscrmid?: boolean;
  userRole?: string[];
  fullProcessing?: boolean; // Whether to run full business logic or minimal
  caseSubType?: { requires_scrm: string }; // Combined case type and sub type for specific logic handling
}

/**
 * Process the successful opportunity API response
 * Handles all Redux state updates and business logic based on the response data
 * 
 * @param response - The response object from OOT.getOpportunity API
 * @param context - Context containing case type, sub type, and other flags
 * @param dispatch - Redux dispatch function
 * @param state - Current Redux state
 */
export const processOpportunitySuccess = (
  response: any,
  context: OpportunityProcessorContext,
  dispatch: Dispatch,
  state: any
) => {
  const { caseType, subType, noscrmid = false, userRole = [], fullProcessing = true } = context;

  // ========================================
  // STEP 1: Update Core Opportunity State
  // ========================================
  dispatch(setStatus(STATUS.IDLE));
  dispatch(setLive(true));

  // Clone response data to handle currency_check flag
  const responseData = _.cloneDeep(response.data);
  if (response.data?.currency_check) {
    responseData.currency.default = "";
  }
  if (response.oot_status) {
    responseData.oot_status = response.oot_status;
  }
  dispatch(setOOTAuth(responseData));
  dispatch(setOOTStatus(STATUS.IDLE));

  // ========================================
  // STEP 2: Process Sell-to-Parties
  // Transform and store sell-to-party information
  // ========================================
  if (Array.isArray(response.data.sell_to_parties)) {
    dispatch(
      handleSellToParty(
        response.data.sell_to_parties.map((party: any) => ({
          id: party.nid,
          value: party.name,
          account_id: party.account_id,
        }))
      )
    );
  }

  // ========================================
  // STEP 3: Set Case Category Based on User Role
  // Different user roles have different default case categories
  // ========================================
  if (fullProcessing) {
    if (!_.includes(userRole, "technical_sales_sales")) {
      dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BUDG }));
    } else {
      dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BIND }));
    }
  }

  // ========================================
  // STEP 4: Process Network IDs
  // Extract and transform network IDs from end customer data
  // Show warning if networks are expected but not found
  // ========================================
  if (
    response.data.end_customer?.network_ids &&
    typeof response.data.end_customer.network_ids === "object" &&
    Object.keys(response.data.end_customer.network_ids).length > 0
  ) {
    const responseNetworkIds = response.data.end_customer.network_ids;
    const networkids = Object.keys(responseNetworkIds).map((key) => ({
      id: key,
      value: responseNetworkIds[key],
    }));
    dispatch(setNetworkIds(networkids));
  } else if (fullProcessing) {
    // Check if network IDs are expected for this case type/subtype combination
    const casetypes = ["non_commercial_network", "internal_order"];
    const subtypes = [
      "subscriptions_renewal",
      "extension_commercial_network",
      "sw_small_item_addition",
    ];
    
    if (
      (casetypes.includes(caseType) && subtypes.includes(subType)) ||
      caseType === "addition_to_existing_edge"
    ) {
      dispatch(
        setError({
          key: "warn",
          value: {
            errorMsg: "No Network found.",
            errorCode: null,
          },
        })
      );
    }
  }

  // ========================================
  // STEP 5: Execute Business Logic Handlers
  // Run specialized handlers for account setup and internal case handling
  // ========================================
  if (fullProcessing) {
    // Handle R6 Internal exceptions - sets Nokia account for internal cases
    handleExceptionR6InternalLogic(state);

    // Handle account logic based on sales channel and other business rules
    handleAccountLogic(response, state);
  }

  // ========================================
  // STEP 6: Handle Error Code 204
  // Special handling for "No Content" response
  // ========================================
  if (response.errorCode && response.errorCode === 204) {
    dispatch(
      setError({
        key: "warn",
        value: {
          errorMsg: "Please enter the offer details manually.",
          errorCode: null,
        },
      })
    );
    dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BUDG }));
  }

  // ========================================
  // STEP 7: Handle OOT Status Messages
  // Process warnings and info messages from the OOT service
  // ========================================
  if (response.oot_status && response.errorCode === "") {
    if (
      (response.oot_status.type === "warning" ||
        (response.oot_status.type === "info" &&
          response.oot_status.description.includes("INVALID_"))) &&
      !noscrmid && context.caseSubType?.requires_scrm?.toLowerCase() === "yes"
    ) {
      const description =
        response.oot_status.type === "info"
          ? "Please add details manually"
          : "";
      dispatch(
        setError({
          key: "warn",
          value: {
            errorMsg: response.oot_status.description + " " + description,
            errorCode: null,
          },
        })
      );
      dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BUDG }));
    } else if (noscrmid) {
      dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BUDG }));
    }
  }
  
  // ========================================
  // STEP 8: Mark Form as Loaded
  // Set flag to indicate form has been loaded after CRM data fetch
  // ========================================
  dispatch(setFormLoaded(true));
};

/**
 * Process failed opportunity API response
 * Handles error states and displays appropriate error messages
 * 
 * @param response - The failed response object
 * @param dispatch - Redux dispatch function
 */
export const processOpportunityFailure = (
  response: any,
  dispatch: Dispatch
) => {
  dispatch(
    setError({
      key: "error",
      value: {
        errorCode: response.errorCode,
        errorMsg: response.errorMessage,
      },
    })
  );
  dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BIND }));
};

/**
 * Process opportunity API exception (network errors, timeouts, etc.)
 * 
 * @param error - The error object
 * @param dispatch - Redux dispatch function
 */
export const processOpportunityException = (
  error: any,
  dispatch: Dispatch
) => {
  dispatch(setStatus(STATUS.IDLE));
  dispatch(
    setError({
      key: "error",
      value: {
        errorCode: error,
        errorMsg: "Timeout",
      },
    })
  );
  dispatch(setLive(false));
  dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BIND }));
};

/**
 * Validate and prepare sCRM ID for API call
 * Returns the actual sCRM ID to use or a fallback value
 * 
 * @param scrmId - The sCRM ID from user input
 * @param caseType - The selected case type
 * @param scrmRegex - Regular expression for sCRM ID validation
 * @param dispatch - Redux dispatch function
 * @returns Object containing the prepared sCRM ID and flags
 */
export const prepareScrmId = (
  scrmId: string,
  caseType: string,
  scrmRegex: RegExp,
  dispatch: Dispatch
): { scrmId: string; noscrmid: boolean } => {
  let preparedScrmId = "19.XY.123456"; // Fallback sCRM ID
  let noscrmid = false;

  // Check if case type doesn't require sCRM ID
  if (nonSCRMCaseTypes.includes(caseType)) {
    return { scrmId: preparedScrmId, noscrmid: true };
  }

  // Validate sCRM ID
  if (!scrmId || scrmId.length === 0) {
    noscrmid = true;
    dispatch(setOBJ({ path: `caseCategory`, value: caseCategory.BUDG }));
    dispatch(
      setError({
        key: "warn",
        value: {
          errorMsg: "No sCRM ID. Please enter the offer details manually.",
          errorCode: null,
        },
      })
    );
  } else if (scrmRegex.test(scrmId)) {
    preparedScrmId = scrmId;
  }

  return { scrmId: preparedScrmId, noscrmid };
};
